import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import {
  GET_CUSTOMER,
  GET_CUSTOMERS,
  DELETE_CUSTOMERS,
  UPDATE_CUSTOMER,
  GET_CUSTOMER_CASES,
  VALIDATE_DEPENDENCIES,
  CALCULATE_INSTALMENTS,
  GET_CUSTOMER_INFO,
  GET_CUSTOMER_BASIC_INFO,
  UPDATE_CUSTOMER_BASIC_INFO,
  UPDATE_CUSTOMER_LOCATION_INFO,
  GET_CUSTOMER_PAYMENTS,
  GET_OPERATIONS_BY_CUSTOMER,
  GET_CUSTOMER_ACTIVITY,
  SEND_EMAIL_USAGE,
  SEND_SMS_USAGE,
  GET_CUSTOMER_KPIS,
  GET_CUSTOMERS_NEW,
} from './customers.GraphQL';
import { CustomerCase, CustomerCriteriaModel } from '@models/customer.model';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  firstValueFrom,
  of,
  throwError,
  mergeMap,
  forkJoin,
} from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CurrentUserService } from '@core/services/currentUser.service';
import { PaymentAgreementService } from '@modules/payment-agreement/services/payment-agreement.service';
import { PermissionType } from '@admin/modules/users/models/user.model';
import {
  ChipsFilter,
  FilterType,
  InputFilter,
  SearchCriteriaCursor,
} from '@shared/components/filters/Domain/filter.model';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';

export const GET_TENANT = gql`
  query ($id: ID!) {
    tenant(id: $id) {
      id
      name
      timezone
      language
      logoUrl
      operationType
      importStrategy {
        snapshot
        autoCommit
      }
    }
  }
`;

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  isLoading$ = new BehaviorSubject<boolean>(false);

  constructor(
    private apollo: Apollo,
    private http: HttpClient,
    private currentUser: CurrentUserService,
    private paymentAgreementService: PaymentAgreementService,
    private translate: TranslateService,
    private route: ActivatedRoute,
  ) {}

  getTenant(id: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.apollo
        .query({
          query: GET_TENANT,
          variables: {
            id: id,
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) {
            reject(res);
          } else {
            resolve(res.data.tenant);
          }
        });
    });
  }

  getCustomer(id: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.apollo
        .query({
          query: GET_CUSTOMER,
          variables: {
            id: id,
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) {
            reject(res);
          } else {
            resolve(res.data.customer);
          }
        });
    });
  }

  getCustomerInfo(id: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.apollo
        .query({
          query: GET_CUSTOMER_INFO,
          variables: {
            id,
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) {
            reject(res);
          } else {
            resolve(res.data.customer);
          }
        });
    });
  }

  getCustomerBasicInfo(id: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.apollo
        .query({
          query: GET_CUSTOMER_BASIC_INFO,
          variables: {
            id,
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) {
            reject(res);
          } else {
            resolve(res.data.customer);
          }
        });
    });
  }

  updateCustomerBasicInfo(customerId: string, input: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.apollo
        .mutate({
          mutation: UPDATE_CUSTOMER_BASIC_INFO,
          variables: {
            id: customerId,
            input: input,
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) {
            reject(res);
          } else {
            resolve(res.data);
          }
        });
    });
  }

  getCustomerLocationInfo(id: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.apollo
        .query({
          query: GET_CUSTOMER_BASIC_INFO,
          variables: {
            id,
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) {
            reject(res);
          } else {
            resolve(res.data.customer);
          }
        });
    });
  }

  updateCustomerLocationInfo(customerId: string, input: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.apollo
        .mutate({
          mutation: UPDATE_CUSTOMER_LOCATION_INFO,
          variables: {
            id: customerId,
            input: input,
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) {
            reject(res);
          } else {
            resolve(res.data);
          }
        });
    });
  }

  getCustomerCases(tenantId: string, customerId: string): Observable<any> {
    return this.apollo
      .query({
        query: GET_CUSTOMER_CASES,
        variables: {
          tenantId: tenantId,
          customerId: customerId,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(
        mergeMap((res: any) => {
          if (res.errors?.length) {
            throw new Error(res.errors[0].message);
          } else {
            const validationObservables = res.data.customerCases.map((customerCase: any) => {
              if (customerCase.status === 'open') {
                return this.paymentAgreementService.checkvalidateDependencies(customerCase.id).pipe(
                  map((validation: boolean) => ({
                    ...customerCase,
                    validation: validation,
                  })),
                );
              } else {
                return of({
                  ...customerCase,
                  validation: false,
                });
              }
            });

            return validationObservables.length ? forkJoin(validationObservables) : of([]);
          }
        }),
      );
  }

  getCustomerOperations(tenantId: string, customerId: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.apollo
        .query({
          query: GET_OPERATIONS_BY_CUSTOMER,
          variables: {
            criteria: {
              tenantId: tenantId,
              customerId: customerId,
            },
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) {
            reject(res.errors[0].message);
          } else {
            resolve(res.data.customerOperations);
          }
        });
    });
  }

  getCustomerPayments(tenantId: string, customerId: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.apollo
        .query({
          query: GET_CUSTOMER_PAYMENTS,
          variables: {
            criteria: {
              tenantId: tenantId,
              customerId: customerId,
            },
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) {
            reject(res.errors[0].message);
          } else {
            resolve(res.data.customerPayments);
          }
        });
    });
  }

  getCustomersByCursor(criteria: SearchCriteriaCursor): Observable<any> {
    return this.apollo
      .query({
        query: GET_CUSTOMERS_NEW,
        variables: {
          criteria: {
            tenantId: this.route.snapshot.queryParamMap.get('tenant'),
            externalIds: criteria.filters['clientsExternalIds'] ?? [],
            emails: criteria.filters['emails'] ?? [],
            phones: criteria.filters['phones'] ?? [],
            tins: criteria.filters['tins'] ?? [],
            name: criteria.filters['name'] ?? '',
            pagination: criteria.pagination,
          },
        },
      })
      .pipe(
        map((res: any) => {
          if (res.errors?.length) {
            throw res;
          }
          return res.data.customerList;
        }),
        catchError((err) => {
          console.error(err);
          return throwError(err);
        }),
      );
  }

  public transformToFilter(criteria: CustomerCriteriaModel): any {
    return {
      searchPattern: criteria.filters.searchPattern,
      externalId: criteria.filters.externalId,
      nif: criteria.filters.nif,
      collectorIds: criteria.filters.collectorIds,
    };
  }

  deleteCustomers(customerIds: Array<string>): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.apollo
        .mutate({
          mutation: DELETE_CUSTOMERS,
          variables: {
            ids: customerIds,
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) {
            reject(res);
          } else {
            resolve(res.data);
          }
        });
    });
  }

  updateCustomer(customerId: string, input: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.apollo
        .mutate({
          mutation: UPDATE_CUSTOMER,
          variables: {
            id: customerId,
            input: input,
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) {
            reject(res);
          } else {
            resolve(res.data);
          }
        });
    });
  }

  checkvalidateDependencies(_case: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.apollo
        .query({
          query: VALIDATE_DEPENDENCIES,
          variables: {
            customerCaseId: _case.id,
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) {
            reject(res);
          } else {
            const { validatePaymentAgreementsDependencies: v } = res.data;
            _case.validation = v;
            resolve(_case);
          }
        });
    });
  }

  calculatePaymentAgreementInstalments(
    customerCaseId: string,
    numberOfInstalments: number,
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.apollo
        .query({
          query: CALCULATE_INSTALMENTS,
          variables: {
            customerCaseId,
            numberOfInstalments,
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) {
            reject(res);
          } else {
            resolve(res.data);
          }
        });
    });
  }

  async setListValidation(cases: CustomerCase[]) {
    const promises = cases.map((_case) => {
      return this.checkvalidateDependencies(_case);
    });
    const res = await Promise.all(promises);
    return res;
  }

  async getLatLongFromAddress(address: string) {
    try {
      const encodedAddress = encodeURIComponent(address);
      const location = await firstValueFrom(
        this.http
          .get(
            `https://www.mapquestapi.com/geocoding/v1/address?key=${environment.mapQuest}&location=${encodedAddress}`,
          )
          .pipe(
            map((response: any) => {
              return response.results[0].locations[0].latLng;
            }),
          ),
      );

      return [location.lng, location.lat];
    } catch (error) {
      return [2.1597551975614673, 41.38722959152048];
    }
  }

  getCustomerActivity(customerId: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.apollo
        .query({
          query: GET_CUSTOMER_ACTIVITY,
          variables: {
            criteria: {
              filters: {
                customerId,
              },
            },
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) {
            reject(res.errors[0].message);
          } else {
            resolve(res.data.searchCustomerInteractions);
          }
        });
    });
  }

  //NOTIFICATIONS
  private sendNotification(mutationType: any, customerCaseId: string, usage: string) {
    return this.apollo
      .mutate({
        mutation: mutationType,
        variables: {
          input: {
            customerCaseId,
            usage,
          },
        },
      })
      .pipe(
        switchMap((res) => {
          if (res.errors?.length) {
            return of({ error: res.errors });
          }
          return of(res.data);
        }),
      );
  }

  sendEmailPaylinkNotificationUsage(customerCaseId: string, usage: string) {
    return this.sendNotification(SEND_EMAIL_USAGE, customerCaseId, usage);
  }

  sendSmsPaylinkNotificationUsage(customerCaseId: string, usage: string) {
    return this.sendNotification(SEND_SMS_USAGE, customerCaseId, usage);
  }

  getCustomerKpis(customerId: string) {
    return new Promise<any>((resolve, reject) => {
      return this.apollo
        .query({
          query: GET_CUSTOMER_KPIS,
          variables: {
            customerId: customerId,
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) {
            reject(res);
          } else {
            resolve(res.data);
          }
        });
    });
  }

  // UTILS ACTIVITIES
  getColor(category: string, eventName: string): string {
    const colorMap: Record<string, Record<string, string>> = {
      payment_order: {
        payment_order_created: 'rgb(238,242,255)',
        payment_order_canceled: 'rgb(254,242,242)',
        payment_order_captured: 'rgb(240,253,244)',
      },
      paylink_notification: {
        paylink_notification_error: 'rgba(240, 60, 60, 0.35)',
        paylink_notification_visited: 'rgb(255,247,237)',
        paylink_notification_discarded: 'rgb(255,247,237)',
        paylink_notification_login_visited: 'rgba(252, 178, 23, 0.35)',
        paylink_notification_login_failed: 'rgba(240, 60, 60, 0.35)',
        paylink_notification_sent: 'rgb(243,251,253)',
        paylink_notification_received: 'rgb(254,246,250)',
        paylink_notification_read: 'rgba(111, 207, 151, 0.7)',
      },
      feedback: {
        feedback_sent: 'rgb(255,247,237)',
        feedback_visited: 'rgb(255,247,237)',
      },
    };
    return colorMap[category]?.[eventName] ?? 'rgba(161, 161, 161, 0.7)';
  }

  getIcon(category: string, eventName: string): string {
    const iconMapping: any = {
      payment_order: {
        payment_order_created: 'pi pi-bolt',
        payment_order_canceled: 'pi pi-times',
        payment_order_captured: 'pi pi-dollar',
      },
      paylink_notification: {
        paylink_notification_error: 'pi pi-times',
        paylink_notification_discarded: 'pi pi-file-excel',
        paylink_notification_visited: 'pi pi-eye',
        paylink_notification_login_visited: 'pi pi-sign-in',
        paylink_notification_login_failed: 'pi pi-sign-out',
        paylink_notification_received: 'pi pi-inbox',
        default: 'pi pi-send',
      },
      feedback: {
        feedback_sent: 'pi pi-comment',
        feedback_visited: 'pi pi-eye',
      },
    };

    if (!iconMapping[category]) {
      return '';
    }

    return iconMapping[category][eventName] || iconMapping[category].default || '';
  }

  getName(eventName: string): string {
    const eventTranslations: Record<string, string> = {
      payment_order_created: 'ha iniciado un intento de pago',
      payment_order_canceled: 'ha cancelado el intento de pago',
      payment_order_captured: 'ha pagado',
      paylink_notification_error: 'Error notificación',
      paylink_notification_visited: 'ha visitado ',
      paylink_notification_login_visited: 'Login',
      paylink_notification_login_failed: 'Login error',
      paylink_notification_sent: 'ha enviado notificación desde',
      paylink_notification_received: 'ha recibido notificación desde',
      paylink_notification_read: 'Notificación leída',
      paylink_notification_discarded: 'Notificación descartada',
      feedback_sent: 'ha alegado',
      feedback_visited: 'ha visitado ',
      api: 'API',
      campaign: 'campaña',
      payment_agreement: 'acuerdo de pago',
      reminder: 'enlace de pago', //QA: change by reminder (?)
    };

    return eventTranslations[eventName] ?? eventName;
  }

  getIconColor(category: string, eventName: string): string {
    const colorMap: Record<string, Record<string, string>> = {
      payment_order: {
        payment_order_created: 'rgb(99,102,241)',
        payment_order_canceled: 'rgb(185,28,28)',
        payment_order_captured: 'rgb(34,197,94)',
      },
      paylink_notification: {
        paylink_notification_error: 'rgba(255, 0, 0, 1)',
        paylink_notification_visited: 'rgb(249,115,22)',
        paylink_notification_discarded: 'rgb(249,115,22)',
        paylink_notification_login_visited: 'rgba(252, 178, 23, 0.95)',
        paylink_notification_login_failed: 'rgba(255, 0, 0, 1)',
        paylink_notification_sent: 'rgb(4,127,148)',
        paylink_notification_received: 'rgb(165,50,107)',
        paylink_notification_read: 'rgba(66, 173, 100, 1)',
      },
      feedback: {
        feedback_sent: 'rgb(249,115,22)',
        feedback_visited: 'rgb(249,115,22)',
      },
    };
    return colorMap[category]?.[eventName] ?? 'var(--primary-color)';
  }

  canWrite() {
    return this.currentUser.allowAccessPermission([
      PermissionType.FULL_ACCESS,
      PermissionType.CUSTOMER_WRITE,
    ]);
  }

  canRead() {
    return this.currentUser.allowAccessPermission([
      PermissionType.FULL_ACCESS,
      PermissionType.CUSTOMER_READ,
    ]);
  }

  getFilters() {
    return [
      {
        id: 'tins',
        label: this.translate.instant('DATA.MODULES.CUSTOMERS.PAGES.CUSTOMER_LIST.FORM.NIF'),
        type: FilterType.Chips,
        icon: 'pi pi-user',
        tooltip: this.translate.instant('DATA.COMMON.FILTER.CHIPS', {
          nameFilter: 'NIF/DNI de cliente',
          ejemplos: '45789145R, 34919145P ...',
        }),
      } as ChipsFilter,
      {
        id: 'clientsExternalIds',
        label: this.translate.instant(
          'DATA.MODULES.CUSTOMERS.PAGES.CUSTOMER_LIST.FORM.EXTERNAL_ID',
        ),
        type: FilterType.Chips,
        icon: 'pi pi-sitemap',
        tooltip: this.translate.instant('DATA.COMMON.FILTER.CHIPS', {
          nameFilter: 'ID de clientes',
          ejemplos: 'ID123, ID145 ...',
        }),
      } as ChipsFilter,
      {
        id: 'phones',
        label: this.translate.instant('DATA.MODULES.CUSTOMERS.PAGES.CUSTOMER_LIST.FORM.PHONES'),
        type: FilterType.Chips,
        icon: 'pi pi-phone',
        tooltip: this.translate.instant('DATA.COMMON.FILTER.CHIPS', {
          nameFilter: 'Teléfonos de cliente',
          ejemplos: ' +34605398222, +34605398111 ... Debe buscar con el prefijo del país.',
        }),
      } as ChipsFilter,
      {
        id: 'emails',
        label: 'Emails',
        type: FilterType.Chips,
        icon: 'pi pi-envelope',
        tooltip: this.translate.instant('DATA.COMMON.FILTER.CHIPS', {
          nameFilter: 'Emails de cliente',
          ejemplos: 'mail1@gmail.com, mail2@gmail.com ...',
        }),
      } as ChipsFilter,
      {
        id: 'name',
        label: this.translate.instant('DATA.MODULES.CUSTOMERS.PAGES.CUSTOMER_LIST.FORM.NAME'),
        type: FilterType.Input,
        icon: 'pi pi-comment',
        tooltip:
          "Introduce palabras completas de 3 o más caracteres, no parciales (por ejemplo, usa 'julieta' en lugar de 'juli'). Puedes buscar varias palabras al mismo tiempo separándolas con espacios, guiones, puntos u otros caracteres no alfanuméricos.",
      } as InputFilter,
    ];
  }
}
